import { StorageService } from '@/services/storage';
import type { GameContextType, GameSettings, GameState, GameStats, Position } from '@/types/game';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useReducer } from 'react';
import { puzzles } from '@/game/puzzles';

const initialGameStats: GameStats = {
  levelStartTime: Date.now(),
  stepsTaken: 0,
  timeSpent: 0,
  rocksEncountered: 0,
  lavaEncountered: 0,
  stopsEncountered: 0,
  keyPressSpeed: 0,
  retries: 0,
  score: 0,
  streak: 0,
  perfectLevels: 0,
};

const initialGameSettings: GameSettings = StorageService.loadSettings();

const initialGameState: GameState = {
  level: 0,
  position: [0, 0],
  initialPosition: [0, 0],
  end: [0, 0],
  rocks: [],
  width: 29,
  isPlaying: false,
  isPaused: false,
  gameStats: initialGameStats,
  gameSettings: initialGameSettings,
  totalScore: StorageService.getTotalScore(),
  achievements: StorageService.getAchievements(),
  gameMode: StorageService.getGameMode(),
  completedLevels: StorageService.getCompletedLevels(),
  levelStats: StorageService.getLevelStats(),
  totalLevels: puzzles.length,
  collectibles: [],
  collectedInLevel: 0,
  enemies: [],
  hand: [],
  moveHistory: [],
  frozenTurns: 0,
  scoutMovesLeft: 0,
  isWarpSelecting: false,
};

type GameAction =
  | { type: 'UPDATE_GAME_STATE'; payload: Partial<GameState> }
  | { type: 'RESET_GAME' }
  | {
      type: 'START_LEVEL';
      payload: {
        level: number;
        position: Position;
        end: Position;
        rocks: Array<[number, number, string]>;
      };
    }
  | { type: 'MOVE_PLAYER'; payload: Position }
  | { type: 'UPDATE_STATS'; payload: Partial<GameStats> }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<GameSettings> }
  | { type: 'COMPLETE_LEVEL'; payload: { score: number; perfect: boolean } }
  | { type: 'GAME_OVER' }
  | { type: 'PAUSE_GAME' }
  | { type: 'RESUME_GAME' };

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'UPDATE_GAME_STATE':
      return { ...state, ...action.payload };

    case 'RESET_GAME':
      return {
        ...initialGameState,
        gameSettings: state.gameSettings,
        totalScore: state.totalScore,
        achievements: state.achievements,
      };

    case 'START_LEVEL':
      return {
        ...state,
        level: action.payload.level,
        position: action.payload.position,
        initialPosition: action.payload.position,
        end: action.payload.end,
        rocks: action.payload.rocks,
        isPlaying: true,
        isPaused: false,
        gameStats: {
          ...initialGameStats,
          levelStartTime: Date.now(),
        },
      };

    case 'MOVE_PLAYER':
      return {
        ...state,
        position: action.payload,
        gameStats: {
          ...state.gameStats,
          stepsTaken: state.gameStats.stepsTaken + 1,
        },
      };

    case 'UPDATE_STATS':
      return {
        ...state,
        gameStats: { ...state.gameStats, ...action.payload },
      };

    case 'UPDATE_SETTINGS':
      const newSettings = { ...state.gameSettings, ...action.payload };
      // Persist settings to localStorage
      StorageService.saveSettings(newSettings);
      return {
        ...state,
        gameSettings: newSettings,
      };

    case 'COMPLETE_LEVEL':
      const newTotalScore = state.totalScore + action.payload.score;
      StorageService.saveTotalScore(newTotalScore);

      return {
        ...state,
        totalScore: newTotalScore,
        gameStats: {
          ...state.gameStats,
          score: state.gameStats.score + action.payload.score,
          perfectLevels: action.payload.perfect
            ? state.gameStats.perfectLevels + 1
            : state.gameStats.perfectLevels,
          streak: action.payload.perfect ? state.gameStats.streak + 1 : 0,
        },
        isPlaying: false,
      };

    case 'GAME_OVER':
      return {
        ...state,
        isPlaying: false,
        isPaused: false,
      };

    case 'PAUSE_GAME':
      return {
        ...state,
        isPaused: true,
      };

    case 'RESUME_GAME':
      return {
        ...state,
        isPaused: false,
      };

    default:
      return state;
  }
}

const GameContext = createContext<GameContextType | undefined>(undefined);

interface GameProviderProps {
  children: ReactNode;
}

export function GameProvider({ children }: GameProviderProps) {
  const [gameState, dispatch] = useReducer(gameReducer, initialGameState);

  const updateGameState = (updates: Partial<GameState>) => {
    dispatch({ type: 'UPDATE_GAME_STATE', payload: updates });
  };

  const resetGame = () => {
    dispatch({ type: 'RESET_GAME' });
  };

  const saveGame = () => {
    // Save game state to localStorage
    StorageService.saveGameState({
      level: gameState.level,
      totalScore: gameState.totalScore,
      achievements: gameState.achievements,
      gameMode: gameState.gameMode,
    });
  };

  const loadGame = () => {
    const savedState = StorageService.loadGameState();
    if (savedState) {
      updateGameState(savedState);
    }
  };

  // Debounced auto-save to prevent excessive localStorage writes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveGame();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [gameState.level, gameState.totalScore, gameState.achievements]);

  const contextValue: GameContextType = {
    gameState,
    updateGameState,
    resetGame,
    saveGame,
    loadGame,
  };

  return <GameContext.Provider value={contextValue}>{children}</GameContext.Provider>;
}

export function useGame() {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}
